import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { set, useForm } from 'react-hook-form';

import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  message,
  Modal,
  Row,
  Typography,
} from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container, FormFieldHeading, MessageBox } from '@/components/Elements';
import { IncomeDetailFormType, PrimaryDetailFormType } from '../types';
import {
  DatePickerField,
  InputField,
  ListOptionsField,
  RadioGroupField,
  SelectField,
  TextAreaField,
} from '@/components/Form';
import {
  ADDITIONAL_TAX_BENEFITS,
  ALLOWANCES_TYPES_OPTIONS,
  AUS_PROPERTY_OPTIONS,
  INCOME_SOURCE_OPTIONS,
  NON_PROPERTY_LOAN_OPTIONS,
} from '../constant/optionList';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import {
  AGREE_OPTIONS,
  CITIZENSHIP_OPTIONS,
  FREQUENCY_OPTIONS,
  LOAN_TYPE_OPTIONS,
  MONTHS,
  RELATIONSHIP_OPTIONS,
  WHERE_DID_YOU_HEAR_ABOUT_US_OPTIONS,
  YEARS,
} from '@/constant/options';
import {
  incomeDetailLabelMapper,
  incomeDetailSchema,
} from '../schema/incomeDetailSchema';
import { currencies, getCurrencyByCountry } from '@/utils/currenciesUtils';
import { purchaseFormRoutes } from '../constant/stepperRoutes';
import useScrollToTop from '@/hooks/useScrollTop';
import { IncomeDetailPayload } from '@/pages/mortgage/types/Purchase';
import {
  useGetPurchaseStepDetailQuery,
  usePostPurchaseRequestMutation,
} from '@/pages/mortgage/apis/purchaseAPI';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import StepProgress from '@/components/StepProgress/StepProgress';
import { stepData } from '../constant/stepData';
import { EmployeeFields } from '../components/Fields/EmployeeFields';
import { ContractorField } from '../components/Fields/ContractorField';
import { CompanyField } from '../components/Fields/CompanyField';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { CoapplicantCompanyField } from '../components/Fields/CoapplicantCompanyField';
import { CoapplicantContractorField } from '../components/Fields/CoapplicantContractorField';
import { CoapplicantEmployeeField } from '../components/Fields/CoapplicantEmployeeFields';
import dayjs from 'dayjs';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import MeetingModal from '../components/PurchaseMeetingModal';
import NextStepModal from '../components/NextStepModal';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import FormSubLabel from '@/components/Elements/Heading/FormSubLabel';
import makeAllFieldsOptional from '@/utils/yupUtils';
import { ObjectSchema } from 'yup';
import ReviewData from '../components/ReviewModal';
import { useAppDispatch } from '@/hooks/hooks';
import { setIncomeFormField } from '@/store/slices/purchase/purchaseSlice';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { coapplicantKeys } from '../constant/incomeDetailKeys';

const IncomeDetail = () => {
  const { id, update } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const total = 6;
  const countryOptions = useCountryOptions();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const termsRef = useRef<HTMLInputElement>(null);

  const [postPurchaseRequest, { isLoading: postPurchaseRequestLoading }] =
    usePostPurchaseRequestMutation();

  const [postPurchaseDraft, { isLoading: postPurchaseDraftLoading }] =
    usePostPurchaseRequestMutation();

  const incomeFormState = useSelector((state: RootState) => state.purchase);
  const { incomeDetail, dataPopulated } = incomeFormState;

  const { data: userProfileData } = useFetchUserProfileQuery(undefined, {
    skip: !id || dataPopulated,
  });

  const {
    data: incomeDetailApiResponse,
    error: fetchIncomeDetailError,
    isLoading: fetchIncomeDetailLoading,
  } = useGetPurchaseStepDetailQuery(
    { application_id: id!, step: 'income_detail' },
    { skip: !id || !update, refetchOnMountOrArgChange: true },
  );

  const incomeDetailData = incomeDetailApiResponse?.data
    .data as IncomeDetailFormType;

  const {
    data: primaryDetailApiResponse,
    error: fetchPrimaryDetailError,
    isLoading: fetchPrimaryDetailLoading,
  } = useGetPurchaseStepDetailQuery(
    { application_id: id!, step: 'primary_detail' },
    { skip: !id, refetchOnMountOrArgChange: true },
  );
  const primaryDetailData = primaryDetailApiResponse?.data
    .data as PrimaryDetailFormType;

  const incomeDetailSchemaWithOptionalFields =
    makeAllFieldsOptional(incomeDetailSchema);

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    unregister,
    formState: { errors },
    resetField,
    reset,
  } = useForm<IncomeDetailFormType>({
    defaultValues: {
      salaryCurrency: '',
      creditCardCurrency: '',
      coapplicantCreditCardCurrency: '',
      coapplicantExists: undefined,
      incomeSource: undefined,
    },
    resolver: yupResolver(
      incomeDetailSchemaWithOptionalFields as ObjectSchema<any>,
    ),
    shouldFocusError: true,
  });

  useEffect(() => {
    if (primaryDetailData?.country) {
      const currency =
        getCurrencyByCountry(primaryDetailData.country)?.currency || '';
      const coapplicantCurrency =
        getCurrencyByCountry(primaryDetailData.coapplicantCountry)?.currency ||
        '';

      setValue('salaryCurrency', 'AUD');
      setValue('creditCardCurrency', currency);
      setValue('coapplicantCreditCardCurrency', coapplicantCurrency);
    }
  }, [
    primaryDetailData?.country,
    reset,
    primaryDetailData?.coapplicantCountry,
  ]);

  useEffect(() => {
    console.log(
      'primaryDetailData?.purchasingUnder',
      primaryDetailData?.purchasingUnder,
    );
    if (
      primaryDetailData?.purchasingUnder &&
      primaryDetailData?.purchasingUnder !== 'solo_borrower'
    ) {
      setValue('coapplicantExists', true);
    }
    console.log('coapplicantExists', watch('coapplicantExists'));
  }, [primaryDetailData?.purchasingUnder]);

  useEffect(() => {
    if (incomeDetailData) {
      setValue('incomeSource', incomeDetailData?.incomeSource);
      console.log(
        '[API] incomeDetailData.incomeSource',
        incomeDetailData?.incomeSource,
      );
      if (incomeDetailData?.incomeSource === 'employee') {
        setValue('jobStartMonth', incomeDetailData?.jobStartMonth);
        setValue('jobStartYear', incomeDetailData?.jobStartYear);
        setValue('occupation', incomeDetailData?.occupation);
        setValue('employer', incomeDetailData?.employer);
        setValue('salaryCurrency', incomeDetailData?.salaryCurrency);
        setValue('baseSalary', incomeDetailData?.baseSalary);
        setValue('salaryFrequency', incomeDetailData?.salaryFrequency);
        setValue(
          'receiveAllowanceIncome',
          incomeDetailData?.receiveAllowanceIncome,
        );
        if (incomeDetailData?.receiveAllowanceIncome) {
          setValue('allowanceIncome', incomeDetailData?.allowanceIncome);
          setValue(
            'typeOfAllowanceIncome',
            incomeDetailData?.typeOfAllowanceIncome,
          );
          setValue('allowanceFrequency', incomeDetailData?.allowanceFrequency);
        }
        setValue(
          'receiveCommissionIncome',
          incomeDetailData?.receiveCommissionIncome,
        );
        if (incomeDetailData?.receiveCommissionIncome) {
          setValue('commissionYears', incomeDetailData?.commissionYears);
          setValue(
            'commissionFrequency',
            incomeDetailData?.commissionFrequency,
          );
          setValue('commissionIncome', incomeDetailData?.commissionIncome);
        }
        setValue(
          'receiveAnnualCashBonus',
          incomeDetailData?.receiveAnnualCashBonus,
        );
        if (incomeDetailData?.receiveAnnualCashBonus) {
          setValue(
            'lastYearAnnualCashBonus',
            incomeDetailData?.lastYearAnnualCashBonus,
          );
          setValue(
            'thisYearAnnualCashBonus',
            incomeDetailData?.thisYearAnnualCashBonus,
          );
        }
        setValue('receiveStockUnit', incomeDetailData?.receiveStockUnit);
        if (incomeDetailData?.receiveStockUnit) {
          setValue(
            'thisYearStockOptions',
            incomeDetailData?.thisYearStockOptions,
          );
          setValue(
            'lastYearStockOptions',
            incomeDetailData?.lastYearStockOptions,
          );
        }
      }

      if (incomeDetailData?.incomeSource === 'self_employed_company') {
        setValue('industry', incomeDetailData?.industry);
        setValue('operationStartMonth', incomeDetailData?.operationStartMonth);
        setValue('operationStartYear', incomeDetailData?.operationStartYear);
        setValue(
          'thisYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.thisYearPersonalNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.lastYearPersonalNetIncomeAfterTaxes,
        );
        setValue('ownershipPercentage', incomeDetailData?.ownershipPercentage);
        setValue(
          'thisYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.thisYearCompanyNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.lastYearCompanyNetIncomeAfterTaxes,
        );
      }

      if (
        incomeDetailData?.incomeSource === 'self_employed_trader_or_contractor'
      ) {
        setValue('operationStartMonth', incomeDetailData?.operationStartMonth);
        setValue('operationStartYear', incomeDetailData?.operationStartYear);
        setValue(
          'thisYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.thisYearPersonalNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.lastYearPersonalNetIncomeAfterTaxes,
        );
        setValue('ownershipPercentage', incomeDetailData?.ownershipPercentage);
        setValue(
          'thisYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.thisYearCompanyNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.lastYearCompanyNetIncomeAfterTaxes,
        );
        setValue('industry', incomeDetailData?.industry);
        setValue(
          'companySalaryCurrency',
          incomeDetailData?.companySalaryCurrency,
        );
      }
      setValue('nonPropertyLoan', incomeDetailData?.nonPropertyLoan);
      setValue('creditCardCurrency', incomeDetailData?.creditCardCurrency);
      setValue('creditCardLimit', incomeDetailData?.creditCardLimit);
      setValue('userMessage', incomeDetailData?.userMessage);
      setValue('taxBenefitAwareness', incomeDetailData?.taxBenefitAwareness);

      setValue(
        'whereDidYouHearAboutUs',
        incomeDetailData?.whereDidYouHearAboutUs,
      );

      if (incomeDetailData?.receiveAnnualCashBonus) {
        setValue(
          'lastYearAnnualCashBonus',
          incomeDetailData?.lastYearAnnualCashBonus,
        );
        setValue(
          'thisYearAnnualCashBonus',
          incomeDetailData?.thisYearAnnualCashBonus,
        );
      }

      if (incomeDetailData?.receiveStockUnit) {
        setValue(
          'thisYearStockOptions',
          incomeDetailData?.thisYearStockOptions,
        );
        setValue(
          'lastYearStockOptions',
          incomeDetailData?.lastYearStockOptions,
        );
      }

      if (incomeDetailData.nonPropertyLoan > 0) {
        for (let i = 1; i <= incomeDetailData.nonPropertyLoan; i++) {
          const propertyTypeField =
            `nonPropertyLoanType${i}` as keyof IncomeDetailFormType;
          const propertyCurrency =
            `nonPropertyLoanCurrency${i}` as keyof IncomeDetailFormType;
          const propertyBalance =
            `nonPropertyLoanBalance${i}` as keyof IncomeDetailFormType;
          const repaymentTerm =
            `nonPropertyLoanRepaymentTerm${i}` as keyof IncomeDetailFormType;
          const repaymentSum =
            `nonPropertyLoanRepaymentSum${i}` as keyof IncomeDetailFormType;
          const repaymentFrequency =
            `nonPropertyLoanRepaymentFrequency${i}` as keyof IncomeDetailFormType;

          setValue(propertyTypeField, incomeDetailData[propertyTypeField]);
          setValue(propertyCurrency, incomeDetailData[propertyCurrency]);
          setValue(propertyBalance, incomeDetailData[propertyBalance]);
          setValue(repaymentTerm, incomeDetailData[repaymentTerm]);
          setValue(repaymentSum, incomeDetailData[repaymentSum]);
          setValue(repaymentFrequency, incomeDetailData[repaymentFrequency]);
        }
      }

      if (primaryDetailData?.purchasingUnder !== 'solo_borrower') {
        setValue(
          'coapplicantCreditCardCurrency',
          incomeDetailData?.coapplicantCreditCardCurrency,
        );
        setValue(
          'coapplicantCreditCardLimit',
          incomeDetailData?.coapplicantCreditCardLimit,
        );
        setValue(
          'coapplicantVisaSubClass',
          incomeDetailData?.coapplicantVisaSubClass,
        );
        setValue(
          'coapplicantVisaExpiryDate',
          incomeDetailData?.coapplicantVisaExpiryDate,
        );
        setValue(
          'coapplicantNonPropertyLoan',
          incomeDetailData?.coapplicantNonPropertyLoan,
        );
        setValue(
          'coapplicantIncomeSource',
          incomeDetailData?.coapplicantIncomeSource,
        );
        if (incomeDetailData?.coapplicantIncomeSource === 'employee') {
          setValue(
            'coapplicantJobStartMonth',
            incomeDetailData?.coapplicantJobStartMonth,
          );
          setValue(
            'coapplicantJobStartYear',
            incomeDetailData?.coapplicantJobStartYear,
          );
          setValue(
            'coapplicantOccupation',
            incomeDetailData?.coapplicantOccupation,
          );
          setValue(
            'coapplicantEmployer',
            incomeDetailData?.coapplicantEmployer,
          );
          setValue(
            'coapplicantSalaryCurrency',
            incomeDetailData?.coapplicantSalaryCurrency,
          );
          setValue(
            'coapplicantBaseSalary',
            incomeDetailData?.coapplicantBaseSalary,
          );
          setValue(
            'coapplicantSalaryFrequency',
            incomeDetailData?.coapplicantSalaryFrequency,
          );
          setValue(
            'coapplicantReceiveAllowanceIncome',
            incomeDetailData?.coapplicantReceiveAllowanceIncome,
          );
          setValue(
            'coapplicantAllowanceIncome',
            incomeDetailData?.coapplicantAllowanceIncome,
          );
          setValue(
            'coapplicantAllowanceFrequency',
            incomeDetailData?.coapplicantAllowanceFrequency,
          );
          setValue(
            'coapplicantTypeOfAllowanceIncome',
            incomeDetailData?.coapplicantTypeOfAllowanceIncome,
          );
          setValue(
            'coapplicantReceiveCommissionIncome',
            incomeDetailData?.coapplicantReceiveCommissionIncome,
          );
          setValue(
            'coapplicantCommissionIncome',
            incomeDetailData?.coapplicantCommissionIncome,
          );
          setValue(
            'coapplicantCommissionFrequency',
            incomeDetailData?.coapplicantCommissionFrequency,
          );
          setValue(
            'coapplicantCommissionYears',
            incomeDetailData?.coapplicantCommissionYears,
          );
          setValue(
            'coapplicantReceiveAnnualCashBonus',
            incomeDetailData?.coapplicantReceiveAnnualCashBonus,
          );
          if (incomeDetailData?.coapplicantReceiveAnnualCashBonus) {
            setValue(
              'coapplicantThisYearAnnualCashBonus',
              incomeDetailData?.coapplicantThisYearAnnualCashBonus,
            );
            setValue(
              'coapplicantLastYearAnnualCashBonus',
              incomeDetailData?.coapplicantLastYearAnnualCashBonus,
            );
          }
          setValue(
            'coapplicantReceiveStockUnit',
            incomeDetailData?.coapplicantReceiveStockUnit,
          );
          if (incomeDetailData?.coapplicantReceiveStockUnit) {
            setValue(
              'coapplicantThisYearStockOptions',
              incomeDetailData?.coapplicantThisYearStockOptions,
            );
            setValue(
              'coapplicantLastYearStockOptions',
              incomeDetailData?.coapplicantLastYearStockOptions,
            );
          }
        }

        if (
          incomeDetailData?.coapplicantIncomeSource ===
          'self_employed_trader_or_contractor'
        ) {
          setValue(
            'coapplicantOperationStartMonth',
            incomeDetailData?.coapplicantOperationStartMonth,
          );
          setValue(
            'coapplicantOperationStartYear',
            incomeDetailData?.coapplicantOperationStartYear,
          );
          setValue(
            'coapplicantIndustry',
            incomeDetailData?.coapplicantIndustry,
          );
          setValue(
            'coapplicantCompanySalaryCurrency',
            incomeDetailData?.coapplicantCompanySalaryCurrency,
          );
          setValue(
            'coapplicantThisYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearPersonalNetIncomeAfterTaxes,
          );
        }

        if (
          incomeDetailData?.coapplicantIncomeSource === 'self_employed_company'
        ) {
          setValue(
            'coapplicantOperationStartMonth',
            incomeDetailData?.coapplicantOperationStartMonth,
          );
          setValue(
            'coapplicantOperationStartYear',
            incomeDetailData?.coapplicantOperationStartYear,
          );
          setValue(
            'coapplicantIndustry',
            incomeDetailData?.coapplicantIndustry,
          );
          setValue(
            'coapplicantCompanySalaryCurrency',
            incomeDetailData?.coapplicantCompanySalaryCurrency,
          );
          setValue(
            'coapplicantThisYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantOwnershipPercentage',
            incomeDetailData?.coapplicantOwnershipPercentage,
          );
          setValue(
            'coapplicantThisYearCompanyNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearCompanyNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearCompanyNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearCompanyNetIncomeAfterTaxes,
          );
        }

        if (
          incomeDetailData?.coapplicantNonPropertyLoan &&
          incomeDetailData.coapplicantNonPropertyLoan > 0
        ) {
          for (
            let i = 1;
            i <= incomeDetailData.coapplicantNonPropertyLoan;
            i++
          ) {
            const propertyTypeField =
              `coapplicantNonPropertyLoanType${i}` as keyof IncomeDetailFormType;
            const propertyCurrency =
              `coapplicantNonPropertyLoanCurrency${i}` as keyof IncomeDetailFormType;
            const propertyBalance =
              `coapplicantNonPropertyLoanBalance${i}` as keyof IncomeDetailFormType;
            const repaymentTerm =
              `coapplicantNonPropertyLoanRepaymentTerm${i}` as keyof IncomeDetailFormType;
            const repaymentSum =
              `coapplicantNonPropertyLoanRepaymentSum${i}` as keyof IncomeDetailFormType;
            const repaymentFrequency =
              `coapplicantNonPropertyLoanRepaymentFrequency${i}` as keyof IncomeDetailFormType;

            setValue(propertyTypeField, incomeDetailData[propertyTypeField]);
            setValue(propertyCurrency, incomeDetailData[propertyCurrency]);
            setValue(propertyBalance, incomeDetailData[propertyBalance]);
            setValue(repaymentTerm, incomeDetailData[repaymentTerm]);
            setValue(repaymentSum, incomeDetailData[repaymentSum]);
            setValue(repaymentFrequency, incomeDetailData[repaymentFrequency]);
          }
        }
      }
    }
  }, [incomeDetailData]);

  useEffect(() => {
    if (id && userProfileData) {
      const userDetail = userProfileData?.data?.common_data;
      setValue('incomeSource', userDetail.incomeSource ?? '');
      if (userDetail?.incomeSource === 'employee') {
        setValue('jobStartMonth', userDetail?.jobStartMonth);
        setValue('jobStartYear', userDetail?.jobStartYear);
        setValue('occupation', userDetail?.occupation);
        setValue('employer', userDetail?.employer);
        setValue('salaryCurrency', userDetail?.salaryCurrency);
        setValue('baseSalary', userDetail?.baseSalary);
        setValue('salaryFrequency', userDetail?.salaryFrequency);
      }
    }
  }, [userProfileData]);

  const watchIncomeSource = watch('incomeSource');
  const watchSalaryCurrency = watch('salaryCurrency');
  const watchCompanySalaryCurrency = watch('companySalaryCurrency');
  const watchNonProperties = watch('nonPropertyLoan') || 0;
  const watchCreditCardCurrency = watch('creditCardCurrency');
  const watchAllowanceIncome = watch('receiveAllowanceIncome');
  const watchCommissionIncome = watch('receiveCommissionIncome');
  const watchAnnualCashBonus = watch('receiveAnnualCashBonus');
  const watchStockUnit = watch('receiveStockUnit');

  const watchCoapplicantIncomeSource = watch('coapplicantIncomeSource');
  const watchCoapplicantAllowanceIncome = watch(
    'coapplicantReceiveAllowanceIncome',
  );
  const watchCoapplicantCommissionIncome = watch(
    'coapplicantReceiveCommissionIncome',
  );
  const watchCoapplicantAnnualCashBonus = watch(
    'coapplicantReceiveAnnualCashBonus',
  );
  const watchCoapplicantStockUnit = watch('coapplicantReceiveStockUnit');
  const watchCoapplicationNonProperty =
    watch('coapplicantNonPropertyLoan') || 0;
  const watchCoapplicantSalaryCurrency = watch('coapplicantSalaryCurrency');
  const watchCoapplicantCompanySalaryCurrency = watch(
    'coapplicantCompanySalaryCurrency',
  );
  const watchCoapplicantCreditCardCurrency = watch(
    'coapplicantCreditCardCurrency',
  );

  useEffect(() => {
    if (watchIncomeSource) {
      if (watchIncomeSource !== 'employee') {
        resetField('jobStartMonth');
        resetField('jobStartYear');
        resetField('occupation');
        resetField('employer');
        resetField('salaryCurrency');
        resetField('baseSalary');
        resetField('salaryFrequency');
        resetField('receiveAllowanceIncome');
        resetField('allowanceIncome');
        resetField('allowanceFrequency');
        resetField('typeOfAllowanceIncome');
        resetField('receiveCommissionIncome');
        resetField('commissionIncome');
        resetField('commissionFrequency');
        resetField('commissionYears');
        resetField('receiveAnnualCashBonus');
        resetField('thisYearAnnualCashBonus');
        resetField('lastYearAnnualCashBonus');
        resetField('receiveStockUnit');
        resetField('thisYearStockOptions');
        resetField('lastYearStockOptions');
      }

      if (
        watchIncomeSource !== 'self_employed_trader_or_contractor' &&
        watchIncomeSource !== 'self_employed_company'
      ) {
        resetField('operationStartMonth');
        resetField('operationStartYear');
        resetField('industry');
        resetField('companySalaryCurrency');
        resetField('thisYearPersonalNetIncomeAfterTaxes');
        resetField('lastYearPersonalNetIncomeAfterTaxes');
        resetField('ownershipPercentage');
        resetField('thisYearCompanyNetIncomeAfterTaxes');
        resetField('lastYearCompanyNetIncomeAfterTaxes');
      }
    }
  }, [watchIncomeSource, resetField]);

  useEffect(() => {
    if (watchCoapplicantIncomeSource !== 'employee') {
      resetField('coapplicantJobStartMonth');
      resetField('coapplicantJobStartYear');
      resetField('coapplicantOccupation');
      resetField('coapplicantEmployer');
      resetField('coapplicantSalaryCurrency');
      resetField('coapplicantBaseSalary');
      resetField('coapplicantSalaryFrequency');
      resetField('coapplicantReceiveAllowanceIncome');
      resetField('coapplicantAllowanceIncome');
      resetField('coapplicantAllowanceFrequency');
      resetField('coapplicantTypeOfAllowanceIncome');
      resetField('coapplicantReceiveCommissionIncome');
      resetField('coapplicantCommissionIncome');
      resetField('coapplicantCommissionFrequency');
      resetField('coapplicantCommissionYears');
      resetField('coapplicantReceiveAnnualCashBonus');
      resetField('coapplicantThisYearAnnualCashBonus');
      resetField('coapplicantLastYearAnnualCashBonus');
      resetField('coapplicantReceiveStockUnit');
      resetField('coapplicantThisYearStockOptions');
      resetField('coapplicantLastYearStockOptions');
    }

    if (
      watchCoapplicantIncomeSource !== 'self_employed_trader_or_contractor' &&
      watchCoapplicantIncomeSource !== 'self_employed_company'
    ) {
      resetField('coapplicantOperationStartMonth');
      resetField('coapplicantOperationStartYear');
      resetField('coapplicantIndustry');
      resetField('coapplicantCompanySalaryCurrency');
      resetField('coapplicantThisYearPersonalNetIncomeAfterTaxes');
      resetField('coapplicantLastYearPersonalNetIncomeAfterTaxes');
      resetField('coapplicantOwnershipPercentage');
      resetField('coapplicantThisYearCompanyNetIncomeAfterTaxes');
      resetField('coapplicantLastYearCompanyNetIncomeAfterTaxes');
    }

    if (watchCoapplicantIncomeSource !== 'self_employed_company') {
      resetField('coapplicantOwnershipPercentage');
      resetField('coapplicantThisYearCompanyNetIncomeAfterTaxes');
      resetField('coapplicantLastYearCompanyNetIncomeAfterTaxes');
    }
  }, [watchCoapplicantIncomeSource, resetField]);

  const [modalOpen, setModalOpen] = useState(false);
  const [nextStepOpen, setNextStepOpen] = useState(false);

  const handleModalClose = () => {
    setModalOpen(false);
    navigate(`/mortgage/purchase`, { replace: true });
  };

  const handleModalOk = () => {
    navigate(`/mortgage/purchase`, { replace: true });
  };

  const onSubmit = (data: IncomeDetailFormType) => {
    if (!acceptedTerms) {
      if (termsRef.current) {
        termsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      message.error('Please accept the terms and conditions.');
      return;
    }
    setDrawerOpen(true);
  };

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
    // setModalOpen(true);
  };

  const handleBackClick = () => {
    Object.keys(getValues()).forEach((key) => {
      dispatch(
        setIncomeFormField({
          field: key as keyof IncomeDetailFormType,
          value: getValues()[key as keyof IncomeDetailFormType] ?? undefined,
        }),
      );
    });
    const route = `${purchaseFormRoutes['primary_detail']}/${id}`;
    navigate(route);
  };

  const clearNonPropertyLoanFields = (index: number) => {
    const fields = [
      `nonPropertyLoanType${index}`,
      `nonPropertyLoanCurrency${index}`,
      `nonPropertyLoanBalance${index}`,
      `nonPropertyLoanRepaymentTerm${index}`,
      `nonPropertyLoanRepaymentSum${index}`,
      `nonPropertyLoanRepaymentFrequency${index}`,
    ];

    fields.forEach((field) => {
      unregister(field as keyof IncomeDetailFormType);
    });
  };

  const clearCoapplicantNonPropertyLoanFields = (index: number) => {
    const fields = [
      `coapplicantNonPropertyLoanType${index}`,
      `coapplicantNonPropertyLoanCurrency${index}`,
      `coapplicantNonPropertyLoanBalance${index}`,
      `coapplicantNonPropertyLoanRepaymentTerm${index}`,
      `coapplicantNonPropertyLoanRepaymentSum${index}`,
      `coapplicantNonPropertyLoanRepaymentFrequency${index}`,
    ];

    fields.forEach((field) => {
      unregister(field as keyof IncomeDetailFormType);
    });
  };

  useEffect(() => {
    if (watchCoapplicationNonProperty) {
      const currentNonProperty = watchCoapplicationNonProperty || 0;
      const previousNonPropertyValue = Object.keys(getValues()).filter((key) =>
        key.startsWith('coapplicantNonPropertyLoan'),
      ).length;

      if (currentNonProperty < previousNonPropertyValue) {
        for (
          let i = currentNonProperty + 1;
          i <= previousNonPropertyValue;
          i++
        ) {
          clearCoapplicantNonPropertyLoanFields(i);
        }
      }
    }
  }, [watchCoapplicationNonProperty, unregister]);

  useEffect(() => {
    if (watchNonProperties) {
      const currentNonProperty = watchNonProperties || 0;
      const previousNonPropertyValue = Object.keys(getValues()).filter((key) =>
        key.startsWith('nonPropertyLoan'),
      ).length;

      if (currentNonProperty < previousNonPropertyValue) {
        for (
          let i = currentNonProperty + 1;
          i <= previousNonPropertyValue;
          i++
        ) {
          clearNonPropertyLoanFields(i);
        }
      }
    }
  }, [watchNonProperties, unregister]);

  const [applicantIndex, setApplicantIndex] = useState(0);
  const [coapplicantIndex, setCoapplicantIndex] = useState(0);

  useEffect(() => {
    if (watchIncomeSource === 'self_employed_trader_or_contractor') {
      setApplicantIndex(5);
    } else if (watchIncomeSource === 'self_employed_company') {
      setApplicantIndex(7);
    } else if (watchIncomeSource === 'employee') {
      setApplicantIndex(9);
    } else {
      setApplicantIndex(2);
    }
  }, [watchIncomeSource]);

  useEffect(() => {
    if (watchCoapplicantIncomeSource === 'self_employed_trader_or_contractor') {
      setCoapplicantIndex(5);
    } else if (watchCoapplicantIncomeSource === 'self_employed_company') {
      setCoapplicantIndex(7);
    } else if (watchCoapplicantIncomeSource === 'employee') {
      setCoapplicantIndex(9);
    } else {
      setCoapplicantIndex(2);
    }
  }, [watchCoapplicantIncomeSource]);

  const saveAsDraft = async () => {
    const rawData = getValues();
    const data = incomeDetailSchemaWithOptionalFields.cast(rawData, {
      stripUnknown: true,
    });
    const payload = {
      data,
      draft: true,
      step: 'income_detail' as const,
      ...(id ? { application_id: id } : {}),
    };

    try {
      const res = await postPurchaseDraft(payload);

      if (res?.data?.success) {
        message.success(res?.data?.message || 'Draft saved successfully');
      } else {
        message.error(
          res?.data?.error
            ? JSON.stringify(res.data.error)
            : 'Failed to save draft',
        );
      }
    } catch (error: any) {
      // Handle network/axios errors separately
      const errMsg =
        error?.response?.data?.error ||
        error?.message ||
        'Error while saving as draft';
      message.error(
        typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg),
      );
    }
  };

  const resetNonPropertyLoanFields = () => {
    const nonPropertyLoanFields = Object.keys(getValues()).filter(
      (key) =>
        key.startsWith('nonPropertyLoan') &&
        parseInt(key.slice(-1)) > watchNonProperties,
    );
    nonPropertyLoanFields.forEach((field) => {
      resetField(field as keyof IncomeDetailFormType);
    });
  };

  const resetCoapplicantNonPropertyLoanFields = () => {
    const coapplicantNonPropertyLoanFields = Object.keys(getValues()).filter(
      (key) =>
        key.startsWith('coapplicantNonPropertyLoan') &&
        parseInt(key.slice(-1)) > watchCoapplicationNonProperty,
    );
    coapplicantNonPropertyLoanFields.forEach((field) => {
      resetField(field as keyof IncomeDetailFormType);
    });
  };

  useEffect(() => {
    if (dataPopulated && incomeDetail) {
      Object.keys(incomeDetail).forEach((key) => {
        if (key === 'incomeSource') {
          console.log('watchincomeSource key', incomeDetail[key]);
        }
        setValue(
          key as keyof IncomeDetailFormType,
          incomeDetail[key as keyof IncomeDetailFormType] ?? undefined,
        );
      });
    }
  }, [dataPopulated, incomeDetail]);

  useEffect(() => {
    if (
      primaryDetailData?.purchasingUnder === 'solo_borrower' &&
      dataPopulated
    ) {
      coapplicantKeys.forEach((key) => {
        dispatch(
          setIncomeFormField({
            field: key as keyof IncomeDetailFormType,
            value: null,
          }),
        );
      });
    }
  }, [primaryDetailData, dataPopulated]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      resetCoapplicantNonPropertyLoanFields();
      resetNonPropertyLoanFields();
    }, 100);

    return () => clearTimeout(timeout);
  }, [watchCoapplicationNonProperty, watchNonProperties]);

  useEffect(() => {
    console.log('watchIncomeSource', watchIncomeSource);
  }, [watchIncomeSource]);

  return (
    <StepperFormLayout
      showBackButton={true}
      handleSubmit={handleFormSubmit}
      handleBackClick={handleBackClick}
      nextBtnText="Review Submission"
      isSubmitLoading={postPurchaseRequestLoading}
      showDraftButton={true}
      handleSaveAsDraftClick={saveAsDraft}
      isDraftLoading={postPurchaseDraftLoading}
    >
      <Container width="md">
        <Row>
          <Col xs={24}>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <Row
                style={{
                  paddingBottom: '20px',
                }}
                gutter={[0, 4]} // vertical spacing when stacked
              >
                <Col
                  xs={24}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <StepProgress
                    id={id || ''}
                    completedStep={3}
                    totalSteps={4}
                    formId="purchase_detail"
                    stepData={stepData}
                  />
                </Col>
              </Row>
              <Row gutter={0}>
                <Card
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Applicant Income Details
                    </Typography.Title>
                  }
                  style={{ width: '100%', marginBottom: '20px' }}
                >
                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        index={1}
                        label={incomeDetailLabelMapper['incomeSource']}
                      />
                      <RadioGroupField
                        name="incomeSource"
                        options={INCOME_SOURCE_OPTIONS}
                        error={errors.incomeSource?.message ?? ''}
                        control={control}
                        cols={{ sm: 1, md: 1, lg: 2 }}
                        size="large"
                      />
                    </div>
                  </Col>
                  {watchIncomeSource === 'employee' && (
                    <EmployeeFields
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchSalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                      resetField={resetField}
                    />
                  )}
                  {watchIncomeSource ===
                    'self_employed_trader_or_contractor' && (
                    <ContractorField
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchCompanySalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                    />
                  )}
                  {watchIncomeSource === 'self_employed_company' && (
                    <CompanyField
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchCompanySalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                    />
                  )}

                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        index={`${applicantIndex}`}
                        label={incomeDetailLabelMapper['nonPropertyLoan']}
                      />
                      <RadioGroupField
                        name="nonPropertyLoan"
                        options={NON_PROPERTY_LOAN_OPTIONS}
                        error={errors.nonPropertyLoan?.message ?? ''}
                        control={control}
                        cols={{ sm: 2, md: 3, lg: 6 }}
                        size="large"
                      />
                    </div>
                  </Col>
                  {Array.from({ length: watchNonProperties }, (_, index) => {
                    const fieldIndex = index + 1;
                    return (
                      <Col xs={24} key={fieldIndex}>
                        <div className="form-control">
                          <FormMainLabel
                            label={`Non-property Loan ${fieldIndex} Details`}
                          />
                          <RadioGroupField
                            label="Select your loan type"
                            size="large"
                            name={`nonPropertyLoanType${fieldIndex}`}
                            options={LOAN_TYPE_OPTIONS}
                            error={
                              (errors as any)[
                                `nonPropertyLoanType${fieldIndex}`
                              ]?.message ?? ''
                            }
                            control={control}
                          />
                        </div>
                        <Row gutter={30}>
                          <Col xs={24} md={12}>
                            <SelectField
                              showSearch={true}
                              options={currencies}
                              name={`nonPropertyLoanCurrency${fieldIndex}`}
                              control={control}
                              size="large"
                              label="Currency"
                              placeholder="Select currency type"
                              error={
                                (errors as any)[
                                  `nonPropertyLoanCurrency${fieldIndex}`
                                ]?.message ?? ''
                              }
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Loan Balance"
                              name={`nonPropertyLoanBalance${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanBalance${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Enter your loan balance"
                              size="large"
                              type="number"
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Repayment loan term"
                              name={`nonPropertyLoanRepaymentTerm${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentTerm${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Years left on loan term"
                              size="large"
                              type="number"
                              suffix="Years"
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Repayment"
                              name={`nonPropertyLoanRepaymentSum${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentSum${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Your repayment Sum"
                              size="large"
                              type="number"
                            />
                          </Col>
                          <Col xs={24}>
                            <RadioGroupField
                              size="large"
                              name={`nonPropertyLoanRepaymentFrequency${fieldIndex}`}
                              options={FREQUENCY_OPTIONS}
                              label="Repayment Frequency"
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentFrequency${fieldIndex}`
                                ]?.message ?? ''
                              }
                              control={control}
                            />
                          </Col>
                        </Row>
                      </Col>
                    );
                  })}

                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        index={`${applicantIndex + 1}`}
                        label="What's your total credit card limit?"
                      />
                    </div>
                    <Row gutter={30}>
                      <Col xs={24} md={12}>
                        <SelectField
                          options={currencies}
                          name="creditCardCurrency"
                          control={control}
                          size="large"
                          showSearch={true}
                          label="Credit Card Currency"
                          placeholder="Select currency type"
                          error={errors?.creditCardCurrency?.message ?? ''}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <InputField
                          label="Credit Card Limit Amount"
                          name="creditCardLimit"
                          control={control}
                          error={errors?.creditCardLimit?.message ?? ''}
                          placeholder="Enter your credit card limit"
                          size="large"
                          type="number"
                          prefix={watchCreditCardCurrency ?? ''}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Card>

                <Card
                  hidden={
                    primaryDetailData?.purchasingUnder === 'solo_borrower'
                  }
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Coapplicant Income Details
                    </Typography.Title>
                  }
                  style={{ width: '100%' }}
                >
                  <Col xs={24}>
                    {primaryDetailData?.purchasingUnder !== 'solo_borrower' && (
                      <Row gutter={30}>
                        <Col xs={24}>
                          <FormMainLabel
                            index={1}
                            label="Coapplicant Income Details"
                          />
                        </Col>
                      </Row>
                    )}
                    {primaryDetailData?.purchasingUnder !== 'solo_borrower' && (
                      <>
                        <Row gutter={30}>
                          <Col xs={24}>
                            <RadioGroupField
                              size="large"
                              name="coapplicantIncomeSource"
                              options={INCOME_SOURCE_OPTIONS}
                              control={control}
                              error={
                                errors?.coapplicantIncomeSource?.message ?? ''
                              }
                            />
                          </Col>
                          {watchCoapplicantIncomeSource === 'employee' && (
                            <CoapplicantEmployeeField
                              control={control}
                              errors={errors}
                              watchSalaryCurrency={
                                watchCoapplicantSalaryCurrency
                              }
                              watchAllowanceIncome={
                                watchCoapplicantAllowanceIncome
                              }
                              watchCommissionIncome={
                                watchCoapplicantCommissionIncome
                              }
                              watchAnnualCashBonus={
                                watchCoapplicantAnnualCashBonus
                              }
                              watchStockUnit={watchCoapplicantStockUnit}
                              resetField={resetField}
                            />
                          )}
                          {watchCoapplicantIncomeSource ===
                            'self_employed_trader_or_contractor' && (
                            <CoapplicantContractorField
                              control={control}
                              errors={errors}
                              watchSalaryCurrency={
                                watchCoapplicantCompanySalaryCurrency
                              }
                              watchAllowanceIncome={
                                watchCoapplicantAllowanceIncome
                              }
                              watchCommissionIncome={
                                watchCoapplicantCommissionIncome
                              }
                              watchAnnualCashBonus={
                                watchCoapplicantAnnualCashBonus
                              }
                              watchStockUnit={watchCoapplicantStockUnit}
                            />
                          )}
                          {watchCoapplicantIncomeSource ===
                            'self_employed_company' && (
                            <CoapplicantCompanyField
                              control={control}
                              errors={errors}
                              watchSalaryCurrency={
                                watchCoapplicantCompanySalaryCurrency
                              }
                              watchAllowanceIncome={
                                watchCoapplicantAllowanceIncome
                              }
                              watchCommissionIncome={
                                watchCoapplicantCommissionIncome
                              }
                              watchAnnualCashBonus={
                                watchCoapplicantAnnualCashBonus
                              }
                              watchStockUnit={watchCoapplicantStockUnit}
                            />
                          )}
                          <Col xs={24}>
                            <FormMainLabel
                              index={`${coapplicantIndex}`}
                              label="Does the coapplicant have any non-property loans?"
                            />
                          </Col>
                          <Col xs={24}>
                            <RadioGroupField
                              size="large"
                              name="coapplicantNonPropertyLoan"
                              options={NON_PROPERTY_LOAN_OPTIONS}
                              control={control}
                              error={
                                errors.coapplicantNonPropertyLoan?.message ?? ''
                              }
                              cols={{
                                sm: 2,
                                md: 4,
                                lg: 6,
                              }}
                            />
                          </Col>
                          {Array.from(
                            { length: watchCoapplicationNonProperty },
                            (_, index) => {
                              const fieldIndex = index + 1;
                              return (
                                <Col xs={24} key={fieldIndex}>
                                  <div className=" form-control">
                                    <FormMainLabel
                                      index={`L2.${fieldIndex}`}
                                      label={`Non-property Loan ${fieldIndex} Details`}
                                    />
                                    <RadioGroupField
                                      label="Select your loan type"
                                      size="large"
                                      name={`coapplicantNonPropertyLoanType${fieldIndex}`}
                                      options={LOAN_TYPE_OPTIONS}
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanType${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                      control={control}
                                    />
                                  </div>
                                  <Row gutter={30}>
                                    <Col xs={24} md={12}>
                                      <SelectField
                                        showSearch={true}
                                        options={currencies}
                                        name={`coapplicantNonPropertyLoanCurrency${fieldIndex}`}
                                        control={control}
                                        size="large"
                                        label="Currency"
                                        placeholder="Select currency type"
                                        error={
                                          (errors as any)[
                                            `coapplicantNonPropertyLoanCurrency${fieldIndex}`
                                          ]?.message ?? ''
                                        }
                                      />
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <InputField
                                        label="Loan Balance"
                                        name={`coapplicantNonPropertyLoanBalance${fieldIndex}`}
                                        control={control}
                                        error={
                                          (errors as any)[
                                            `coapplicantNonPropertyLoanBalance${fieldIndex}`
                                          ]?.message ?? ''
                                        }
                                        placeholder="Enter your loan balance"
                                        size="large"
                                        type="number"
                                      />
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <InputField
                                        label="Repayment loan term"
                                        name={`coapplicantNonPropertyLoanRepaymentTerm${fieldIndex}`}
                                        control={control}
                                        error={
                                          (errors as any)[
                                            `coapplicantNonPropertyLoanRepaymentTerm${fieldIndex}`
                                          ]?.message ?? ''
                                        }
                                        placeholder="Years left on loan term"
                                        size="large"
                                        type="number"
                                        suffix="Years"
                                      />
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <InputField
                                        label="Repayment"
                                        name={`coapplicantNonPropertyLoanRepaymentSum${fieldIndex}`}
                                        control={control}
                                        error={
                                          (errors as any)[
                                            `coapplicantNonPropertyLoanRepaymentSum${fieldIndex}`
                                          ]?.message ?? ''
                                        }
                                        placeholder="Your repayment Sum"
                                        size="large"
                                        type="number"
                                      />
                                    </Col>
                                    <Col xs={24}>
                                      <RadioGroupField
                                        size="large"
                                        name={`coapplicantNonPropertyLoanRepaymentFrequency${fieldIndex}`}
                                        options={FREQUENCY_OPTIONS}
                                        label="Repayment Frequency"
                                        error={
                                          (errors as any)[
                                            `coapplicantNonPropertyLoanRepaymentFrequency${fieldIndex}`
                                          ]?.message ?? ''
                                        }
                                        control={control}
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              );
                            },
                          )}
                        </Row>
                        <div className="form-control">
                          <Row gutter={30}>
                            <Col xs={24}>
                              <FormMainLabel
                                index={`${coapplicantIndex + 1}`}
                                label="What's the coapplicant's total credit card limit?"
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <SelectField
                                options={currencies}
                                name="coapplicantCreditCardCurrency"
                                control={control}
                                size="large"
                                showSearch={true}
                                label="Credit Card Currency"
                                placeholder="Select currency type"
                                error={
                                  errors?.coapplicantCreditCardCurrency
                                    ?.message ?? ''
                                }
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <InputField
                                label="Credit Card Limit Amount"
                                name="coapplicantCreditCardLimit"
                                control={control}
                                error={
                                  errors?.coapplicantCreditCardLimit?.message ??
                                  ''
                                }
                                placeholder="Enter your credit card limit"
                                size="large"
                                type="number"
                                prefix={
                                  watchCoapplicantCreditCardCurrency ?? ''
                                }
                              />
                            </Col>
                          </Row>
                        </div>
                      </>
                    )}
                  </Col>
                </Card>
                <Card
                  style={{ width: '100%', marginTop: '20px' }}
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Additional Details
                    </Typography.Title>
                  }
                >
                  <Col xs={24}>
                    <div className=" form-control">
                      <FormMainLabel
                        index={1}
                        label={
                          incomeDetailLabelMapper['whereDidYouHearAboutUs']
                        }
                      />
                      <SelectField
                        name="taxBenefitAwareness"
                        control={control}
                        options={ADDITIONAL_TAX_BENEFITS}
                        label="Are you aware of the tax benefits available to overseas residents with Australian property investments?"
                        error={errors?.taxBenefitAwareness?.message ?? ''}
                        size="large"
                      />

                      <SelectField
                        name="whereDidYouHearAboutUs"
                        showSearch
                        control={control}
                        options={WHERE_DID_YOU_HEAR_ABOUT_US_OPTIONS}
                        label="Where did you hear about us?"
                        error={errors?.whereDidYouHearAboutUs?.message ?? ''}
                        size="large"
                      />
                    </div>
                  </Col>

                  <Col xs={24}>
                    <div className="form-section-header form-control">
                      <FormMainLabel
                        index={2}
                        label={incomeDetailLabelMapper['userMessage']}
                      />
                    </div>
                    <TextAreaField
                      name="userMessage"
                      control={control}
                      error={errors?.userMessage?.message ?? ''}
                      placeholder="Please name 2-3 suburbs you would like to receive suburb reports for. If there is anything else you would like to know, please highlight it here."
                      size="large"
                      rows={4}
                    />
                    <div ref={termsRef} className="terms-and-conditions">
                      <MessageBox style={{ marginTop: '3rem' }}>
                        <Typography.Title level={5} style={{ marginTop: '0' }}>
                          Note: Please ensure you have answered all questions
                          before submitting. The more accurate your answers, the
                          more accurate your assessment.
                        </Typography.Title>
                        <Typography.Paragraph style={{ marginBottom: '0' }}>
                          We are collecting your personal information to assist
                          you with your home loan application or any other
                          ancillary products. By clicking the submit button you
                          have read, consented, and agree to be bound by our{' '}
                          <a
                            href="https://www.odinmortgage.com/privacy-security/"
                            target="_blank"
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'underline',
                            }}
                          >
                            Privacy Policy
                          </a>{' '}
                          &{' '}
                          <a
                            href="https://www.odinmortgage.com/credit-guide/"
                            target="_blank"
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'underline',
                            }}
                          >
                            Credit Guide.
                          </a>
                        </Typography.Paragraph>

                        <Checkbox
                          style={{
                            marginTop: '1.5rem',
                            fontSize: '15px',
                            fontWeight: 500,
                            padding: '10px 14px',
                            border: acceptedTerms
                              ? '1.5px solid #52c41a'
                              : '1.5px solid #d9d9d9',
                            borderRadius: '8px',
                            background: acceptedTerms ? '#f6ffed' : '#ffffff',
                            width: '100%',
                          }}
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                        >
                          I accept the terms and conditions.
                        </Checkbox>
                      </MessageBox>
                    </div>
                  </Col>
                </Card>

                <Col xs={24}>
                  <Button
                    ref={submitButtonRef}
                    type="primary"
                    htmlType="submit"
                    style={{ display: 'none' }}
                  >
                    Submit
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
        <Drawer
          title="Review Submission"
          open={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={1000}
          footer={
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button onClick={() => setDrawerOpen(false)} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                loading={postPurchaseRequestLoading}
                disabled={postPurchaseRequestLoading}
                onClick={handleSubmit((formData) => {
                  const payload: IncomeDetailPayload = {
                    data: formData,
                    step: 'income_detail',
                    ...(id ? { application_id: id } : {}),
                  };

                  postPurchaseRequest(payload)
                    .unwrap()
                    .then((res) => {
                      displaySuccess(res?.message);
                      window.parent.postMessage({ purchase_id: id }, '*');
                      setNextStepOpen(true);
                      setDrawerOpen(false);
                    })
                    .catch((err) => {
                      displayError(err);
                    });
                })}
              >
                Confirm & Submit
              </Button>
            </div>
          }
        >
          <ReviewData
            stepData={[
              {
                step: 'primary_detail',
                data: primaryDetailData,
              },
              {
                step: 'income_detail',
                data: getValues(),
              },
            ]}
            id={id ?? ''}
          />
        </Drawer>

        <NextStepModal
          open={nextStepOpen}
          eyebrow="Purchase application submitted"
          description="Completing the fact-find now helps your broker prepare your application. You can also come back to it later from your applications list."
          options={[
            {
              id: 'factfind',
              title: 'Continue to fact-find',
              body: 'Answer a few more questions about your situation. Takes about 10 minutes, and we prefill what you have already told us.',
              cta: 'Continue to fact-find',
              recommended: true,
            },
            {
              id: 'meeting',
              title: 'Book a meeting with a broker',
              body: 'Pick a time to talk through your application. You can do the fact-find with your broker.',
              cta: 'Book a meeting',
            },
            {
              id: 'later',
              title: 'Do this later',
              body: 'Go back to your purchase applications. The fact-find stays available there.',
              cta: 'Go to my applications',
            },
          ]}
          onConfirm={(choice) => {
            setNextStepOpen(false);
            if (choice === 'factfind') {
              navigate(`/mortgage/purchase/fact-find/1/${id}`, {
                replace: true,
              });
            } else if (choice === 'meeting') {
              setModalOpen(true);
            } else {
              handleModalOk();
            }
          }}
        />
        <MeetingModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onOk={handleModalOk}
        />
      </Container>
    </StepperFormLayout>
  );
};

export default IncomeDetail;
